import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel, EventOptionSet, EventSitemapSet, SetTab } from '@frontend/sports/betting-offer/feature/model';
import { AngstromTagIdValues } from '@frontend/sports/betting-offer/feature/types';
import { EventDetailsConfig, MyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { UrlService } from '@frontend/vanilla/core';
import { find, has } from 'lodash-es';
import { distinctUntilKeyChanged, filter } from 'rxjs';

import { ComponentProxy } from '../../deferred/component-loader.service';
import { DispatcherEvent, FeatureUrl, HeaderContentDensity } from '../../event-details-common/event-details-common.models';
import { EventDetailsHelperService } from '../../event-details-common/event-details-helper.service';
import { HeaderNavigationService } from '../../event-details-common/header-navigation/header-navigation.service';
import { EventMarketsTabBarItem } from '../../event-details-common/header/tab-group/event-header-tab-bar.models';
import { EventDetailsSitemapComponentLoaderService } from '../../event-details-sitemap/event-details-sitemap-component-loader.service';
import { EventDetailsSitemapBinding } from '../../event-details-sitemap/event-details-sitemap.models';
import { AdaptiveLayoutService, AdaptiveLayoutState } from '../../layout/adaptive-layout.service';
import { ColumnLayoutService } from '../../layout/column-layout.service';
import { MyBetsSummaryService } from '../../my-bets-base/my-bets-summary.service';
import { TabBarComponent } from '../../tab-bar/tab-bar.component';
import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { BetBuilderTrackingValues } from '../bet-builder-v2/bet-builder-tracking';
import { EventNavigation, SubNavigationPayload } from '../event-details-dispatcher-events';
import { EventDetailsMarketQueryService } from './event-details-market-query.service';
import { HeaderNavigationUrlService } from './header-navigation-url.service';
import { HeaderService } from './header.service';

@Component({
    selector: 'ms-header',
    templateUrl: 'header.component.html',
})
export class HeaderComponent implements OnChanges, OnDestroy, OnInit, AfterViewInit {
    @Input() isLive: boolean;
    @Input() event: EventModel;
    @Input() optionSets: EventOptionSet[] = [];
    @Input() isNewEDPScoreboard = false;

    @HostBinding('class') className = 'event-detail-header';
    @HostBinding('class.with-header-navigation') withHeaderNavigation = false;

    @HostBinding('class.sticky') sticky: boolean;

    @Input() set: string | number;
    @Output() setChange = new EventEmitter<{
        setId: string | number;
        groupedSetsIds?: number[];
        subSetId?: string;
        tags?: string[];
        isSgp?: boolean;
        defaultTab?: number | string;
    }>();

    @Output() featureChange = new EventEmitter<number>();

    feature: TabBarItem;
    featureTabs: TabBarItem[];
    setTabs: EventMarketsTabBarItem[];
    isSitemapEnabled: boolean;
    sitemapComponent?: ComponentProxy<EventDetailsSitemapBinding>;
    private isVideoPinned = false;
    isHeaderSubNavigationEnabled: boolean;
    private readonly destroyRef = inject(DestroyRef);

    @ViewChildren(TabBarComponent) private tabMenus: QueryList<TabBarComponent>;

    constructor(
        public headerNavigation: HeaderNavigationService,
        private headerService: HeaderService,
        private trackingService: TrackingService,
        private timer: TimerService,
        private myBetsSummary: MyBetsSummaryService,
        private urlService: UrlService,
        private columnLayout: ColumnLayoutService,
        private adaptiveLayout: AdaptiveLayoutService,
        private headerNavigationUrlService: HeaderNavigationUrlService,
        private eventDetailsHelperService: EventDetailsHelperService,
        private changeRef: ChangeDetectorRef,
        private dispatcher: DispatcherService,
        private eventDetailConfig: EventDetailsConfig,
        private mybetsConfig: MyBetsConfig,
        private sitemapLoader: EventDetailsSitemapComponentLoaderService,
        private eventDetailsMarketQueryService: EventDetailsMarketQueryService,
    ) {
        this.isSitemapEnabled = this.eventDetailConfig.enableSitemapNavigation;
        if (this.isSitemapEnabled) {
            this.sitemapComponent = this.sitemapLoader.getEventDetailsSitemapComponent();
            this.sitemapComponent?.subscribeToOutputs({ toggleSitemapItem: (item: EventMarketsTabBarItem) => this.toggleSitemap(item) });
        }

        this.headerNavigation.selectedTab
            .pipe(
                filter((x) => !!x),
                takeUntilDestroyed(),
            )
            .subscribe((x) => this.featureTabSelected(x!));

        this.dispatcher
            .on<SetTab>(DispatcherEvent.EventDetails.SetTab)
            .pipe(takeUntilDestroyed())
            .subscribe((setId) => {
                const newSetId = setId === SetTab.Sgp ? (this.setTabs.find((x) => x.isSgp)?.id ?? 0) : setId;
                if (newSetId === SetTab.BetBuilder && newSetId === this.set) {
                    return;
                }

                this.setTabActivate(newSetId);
                const active = this.setTabs.find((tab) => tab.active);
                if (active) {
                    this.setTabSelected(active);
                }
            });

        this.dispatcher
            .on<SubNavigationPayload>(EventNavigation.SetSubTabs)
            .pipe(takeUntilDestroyed())
            .subscribe((payload) => {
                const tab = this.setTabs.find((t) => t.id === payload.tabId);
                if (tab) {
                    tab.subNavigationTabs = payload.subTabs;
                    this.setTabs = this.setTabs.slice(0);
                }

                if (this.isSitemapEnabled && tab?.id === SetTab.BetBuilder && tab?.active) {
                    this.setBetBuilderSubNavigation(tab, payload);
                }
            });

        this.isHeaderSubNavigationEnabled = this.eventDetailConfig.isHeaderSubNavigationEnabled;
    }

    ngAfterViewInit(): void {
        this.tabMenus.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => this.tabChanged());
    }

    async ngOnInit(): Promise<void> {
        this.adaptiveLayout.stateChange$
            .pipe(distinctUntilKeyChanged('columns'), takeUntilDestroyed(this.destroyRef))
            .pipe(filter((_) => Boolean(this.setTabs && this.setTabs.length)))
            .subscribe(this.layoutColumnsChanged);

        return this.init();
    }

    tabClicked(tabId: string): void {
        this.dispatcher.dispatch(EventNavigation.SelectSubTab, tabId);
        this.setChange.emit({
            setId: this.set,
            subSetId: this.getActiveSubset(),
            isSgp: this.getActiveTab().isSgp,
            defaultTab: this.getDefaultTab(),
        });
    }

    private toggleSitemap(item: EventMarketsTabBarItem): void {
        if (item.isBetBuilderSubItem && item.active) {
            // Since sportcast tabs are loaded lazily and not on fixture creation and because HeaderService expects tabs to be part of EventModel
            // we cannot treat it like regular sitemap which sets queryString, and instead we just need to dispatch EventNavigation.SelectSubTab
            // so that it triggers betbuilder-market-list group change and market list fetching
            this.tabClicked(item.id.toString());

            return;
        }

        if (!item.active) {
            this.eventDetailsMarketQueryService.cleanQueryString();
            this.initSetTabs(false);
        } else {
            this.eventDetailsMarketQueryService.setQueryString(this.setTabs, item);
        }

        const active = item.active ? item : this.getActiveTab();
        const tags = this.getTagsForItem(active);

        this.setChange.emit({
            setId: active.id,
            groupedSetsIds: active.marketGroups,
            tags,
            isSgp: active.isSgp,
            defaultTab: this.getDefaultTab(),
        });
    }

    private initSetTabs(showGoHome: boolean, queryString?: string, buildDefaultTabs?: boolean): void {
        const tags = queryString ? this.eventDetailsMarketQueryService.getTagsFromQueryString(queryString) : [];

        if (tags.length === 0) {
            this.eventDetailsMarketQueryService.cleanQueryString();
        }

        this.setTabs = this.headerService.getSetTabs(
            this.event,
            {
                canAddMybets: this.shouldHaveMybetsTab(this.adaptiveLayout.current),
                canAddBetBuilder: true,
                desiredSetId: this.eventDetailsMarketQueryService.getMarketSetFromQuery(),
            },
            tags,
            buildDefaultTabs ?? (tags && tags.length <= 1),
        );
        this.sitemapComponent?.update({
            items: this.setTabs.filter((t) => !t.defaultPill),
            showGoHome,
            eventFinished: this.event.isFinished(),
            betBuilderProvider: this.event.betBuilderInfo?.betBuilderProvider,
        });
    }

    private async init(): Promise<void> {
        await this.initFeatureTabs();

        const queryString = this.eventDetailsMarketQueryService.getQueryString();
        this.initSetTabs(false, queryString);

        this.eventDetailsMarketQueryService.cleanMyBetsQueryString(this.setTabs);
        this.setTabActivate(this.set);

        if (this.set === -100) {
            // Issue with ExpressionChangedAfterItHasBeenCheckedError
            const active = this.getActiveTab();

            if (active.defaultPill) {
                this.eventDetailsMarketQueryService.cleanQueryString();
            } else if (!!queryString && queryString !== active.queryString) {
                this.eventDetailsMarketQueryService.setQueryString(this.setTabs);
            }

            const tags = this.getTagsForItem(active);

            await this.setChange.emit({
                setId: active.id,
                groupedSetsIds: active.marketGroups,
                subSetId: this.getActiveSubset(),
                tags,
                isSgp: active.isSgp,
                defaultTab: this.getDefaultTab(),
            });

            this.set = active.id;
        }

        this.tabChanged();

        this.myBetsSummary.summaryUpdate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onMyBetsSummaryUpdate());
    }

    ngOnDestroy(): void {
        this.headerNavigation.updateSelected();
    }

    async ngOnChanges(changes: ISimpleChanges<HeaderComponent>): Promise<void> {
        if (changes.event?.currentValue) {
            if (!changes.event.isFirstChange) {
                const liveStateChanged = changes.isLive?.currentValue !== changes.isLive?.previousValue;
                if (liveStateChanged) {
                    this.set = -100;
                    this.initSetTabs(false, this.eventDetailsMarketQueryService.getQueryString());
                }
            }
            this.sitemapComponent?.update({
                trackingData: {
                    eventId: changes.event.currentValue.id,
                    sportId: changes.event.currentValue.sport.id,
                    leagueId: changes.event.currentValue.league.id,
                    isLive: changes.event.currentValue.live,
                    eventName: changes.event.currentValue.name,
                },
            });

            if (changes.event.previousValue && changes.event.currentValue.id !== changes.event.previousValue.id) {
                this.set = -100;
                this.init();

                return;
            }
        }

        const setChange = changes.set;
        if (setChange && !setChange.firstChange) {
            const currentValue = setChange.currentValue;
            const queryString = this.eventDetailsMarketQueryService.getQueryString();

            if (queryString !== undefined) {
                const tab = this.setTabs.find((t) => t.id === currentValue);

                if (tab?.hasChildren) {
                    this.initSetTabs(true, tab.queryString, false);
                } else {
                    this.sitemapComponent?.update({ showGoHome: true });
                }
            }

            this.setTabActivate(currentValue);

            const active = this.getActiveTab();
            if (this.isSgpTabSelected(active)) {
                await this.setChange.emit({
                    setId: active.id,
                    groupedSetsIds: active.marketGroups,
                    tags: this.getTagsForItem(active),
                    isSgp: active.isSgp,
                    defaultTab: this.getDefaultTab(),
                });
            }
        }

        const optionSetChange = changes.optionSets;
        if (optionSetChange && !optionSetChange.firstChange) {
            this.optionSetsChanged();
        }

        const isLiveChange = changes.isLive;
        if (isLiveChange && !isLiveChange.firstChange) {
            await this.initFeatureTabs();
        }
    }

    private async initFeatureTabs(): Promise<void> {
        if (this.event) {
            this.featureTabs = await this.headerNavigation.getTabs(this.event);
        }
    }

    private onMyBetsSummaryUpdate(): void {
        if (this.shouldHaveMybetsTab(this.adaptiveLayout.current)) {
            this.setTabs = this.headerService.getSetTabs(this.event, {
                canAddMybets: true,
                canAddBetBuilder: true,
                currentSetId: this.set,
                currentSubSetId: this.getActiveSubset(),
            });
            this.sitemapComponent?.update({ items: this.setTabs.filter((t) => !t.defaultPill) });
        }
        this.changeRef.detectChanges();
    }

    featureTabSelected(feature: TabBarItem): void {
        this.feature = feature;
        this.featureChange.emit(feature.id);
        this.tabChanged();

        // on desktop don't update url
        if (!this.adaptiveLayout.current.hasRightColumn) {
            this.headerNavigationUrlService.updateCurrentUrl(this.event, feature);
        }
    }

    setTabSelected(active: EventMarketsTabBarItem): void {
        this.eventDetailsMarketQueryService.setQueryString(this.setTabs, active);
        this.setChange.emit({
            setId: active.id,
            groupedSetsIds: active.isSgp ? active.marketGroups : [],
            subSetId: this.getActiveSubset(),
            isSgp: active.isSgp,
            defaultTab: this.getDefaultTab(),
        });
        this.trackSet(+active.id, active.title!);
    }

    densityToggled(density: HeaderContentDensity): void {
        if (density !== HeaderContentDensity.Fullscreen) {
            this.tabChanged();
        }
    }

    private layoutColumnsChanged = (state: AdaptiveLayoutState): void => {
        const tags = this.trySetCorrectTags();
        this.setTabs = this.headerService.getSetTabs(
            this.event,
            {
                canAddMybets: this.shouldHaveMybetsTab(state),
                canAddBetBuilder: true,
                currentSetId: this.set,
                currentSubSetId: this.getActiveSubset(),
            },
            tags,
            tags && tags.length <= 1,
        );

        const selectedSetInList = this.setTabs.some((t) => t.id === this.set);
        if (!selectedSetInList) {
            this.setTabs.forEach((t) => (t.active = t.id === SetTab.All));
            const active = this.getActiveTab();
            this.set = active.id;
            this.setChange.emit({
                setId: active.id,
                groupedSetsIds: active.marketGroups,
                subSetId: this.getActiveSubset(),
                isSgp: active.isSgp,
                defaultTab: this.getDefaultTab(),
            });
        }
    };

    private shouldHaveMybetsTab(state: AdaptiveLayoutState): boolean {
        return state.columns <= 2 && !this.mybetsConfig.isOpenSummaryLoadingRestricted;
    }

    private optionSetsChanged(): void {
        const tags = this.trySetCorrectTags();
        const buildDefaultTabs = tags.length <= 1 && (this.setTabs.every((x) => (x.queryString || '').split(':').length <= 1) || tags.length < 1);

        this.setTabs = this.headerService.getSetTabs(
            this.event,
            {
                canAddMybets: this.shouldHaveMybetsTab(this.adaptiveLayout.current),
                canAddBetBuilder: true,
                currentSetId: this.set,
                desiredSetId: this.eventDetailsMarketQueryService.getMarketSetFromQuery(),
                currentSubSetId: this.getActiveSubset(),
            },
            tags,
            buildDefaultTabs,
        );

        this.setTabActivate(this.set);
        const active = this.getActiveTab();
        this.setChange.emit({
            setId: active.id,
            groupedSetsIds: active.marketGroups,
            subSetId: this.getActiveSubset(),
            tags: this.getTagsForItem(active),
            isSgp: active.isSgp,
            defaultTab: this.getDefaultTab(),
        });
        this.sitemapComponent?.update({
            items: this.setTabs.filter((t) => !t.defaultPill),
            showGoHome: tags.length > 0 || active.showSeparator,
        });
    }

    private tabChanged(): void {
        this.timer.setAnimationFrame(() => this.setStickyHeader());
    }

    private setStickyHeader = (): void => {
        const tab = this.urlService.current().search.get('tab');
        if (tab) {
            if (tab === FeatureUrl.Scoreboard) {
                this.sticky = true;
            }
            if (tab === FeatureUrl.Video) {
                this.sticky = this.isVideoPinned;
            } else {
                this.isVideoPinned = false;
            }
            if (tab === FeatureUrl.Simulation) {
                this.sticky =
                    !this.event.runningBallDataId &&
                    (!this.eventDetailsHelperService.hasBetRadarVisualization(this.event) ||
                        this.eventDetailsHelperService.hasBetRadarStickyAddonVisualization(this.event)) &&
                    !this.eventDetailsHelperService.hasImgAnimationVisualization(this.event);
            }
            if (tab === FeatureUrl.Statistics) {
                this.sticky = false;
            }
        } else {
            this.sticky = !this.adaptiveLayout.current.hasRightColumn;
        }

        this.sticky = this.isNewEDPScoreboard ? this.isNewEDPScoreboard : this.sticky;
    };

    private setTabActivate(active: string | number): void {
        if (active === -100) {
            return;
        }
        const hasActive = find(this.setTabs, (tab) => tab.id === active);

        if (!hasActive) {
            const activeTab = find(this.setTabs, (tab) => !has(SetTab, tab.id)) as TabBarItem;
            active = activeTab && activeTab.id;
            this.set = activeTab && activeTab.id;
        }

        this.setTabs.forEach((tab) => (tab.active = tab.id === active));
        this.setTabs = this.setTabs.slice(0);
    }

    private getActiveTab(): EventMarketsTabBarItem {
        return this.setTabs.filter((tab) => tab.active).shift()!;
    }

    private getDefaultTab(): number | string | undefined {
        return this.setTabs?.find((tab) => tab.defaultPill)?.id;
    }

    private trackSet(set: number, title: string): void {
        if (set === SetTab.BetBuilder) {
            this.trackingService.update({ [trackingConstants.PAGE_REFERRING_ACTION]: 'BetBuilderTab' });
            this.trackingService.track(trackingConstants.EVENT_TRACKING, {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
                [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
                [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
                [trackingConstants.COMPONENT_POSITION_EVENT]: BetBuilderTrackingValues.Tab,
                [trackingConstants.COMPONENT_LOCATION_EVENT]: this.event.name,
                [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.BuildaBet,
                [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
                [trackingConstants.SPORT_ID]: this.event.sport.id.toString(),
                [trackingConstants.SPORT_LEAGUE_ID]: this.event.league.id.toString(),
                [trackingConstants.COMPONENT_MARKET_ID]: set,
            });
        } else if (set === SetTab.MyBets) {
            this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
                [trackingConstants.PAGE_NAME]: 'myBets_Event',
                [trackingConstants.PAGE_SITE_SECTION]: 'eventView',
            });
        } else {
            this.trackingService.track(trackingConstants.EVENT_TRACKING, {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'market switcher',
                [trackingConstants.COMPONENT_LABEL_EVENT]: 'markets',
                [trackingConstants.COMPONENT_ACTION_EVENT]: 'select',
                [trackingConstants.COMPONENT_POSITION_EVENT]: title,
                [trackingConstants.COMPONENT_LOCATION_EVENT]: 'event details page',
                [trackingConstants.COMPONENT_EVENT_DETAILS]: 'market',
                [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
                [trackingConstants.SPORT_ID]: this.event.sport.id.toString(),
                [trackingConstants.SPORT_LEAGUE_ID]: this.event.league.id.toString(),
                [trackingConstants.COMPONENT_MARKET_ID]: set,
            });
        }
    }

    navigateNext(): void {
        if (this.columnLayout.layout.isRightVisible) {
            return;
        }

        this.headerNavigation.goNext();
    }

    navigatePrevious(): void {
        if (this.columnLayout.layout.isRightVisible) {
            return;
        }

        this.headerNavigation.goPrevious();
    }

    onVideoPinToggle(isVideoPinned: boolean): void {
        this.isVideoPinned = isVideoPinned;
        this.setStickyHeader();
    }

    private getActiveSubset(): string | undefined {
        return this.setTabs?.find((tab) => !!tab.active)?.subNavigationTabs?.find((tab) => !!tab.active)?.id;
    }

    private trySetCorrectTags(): string[] {
        const tags = this.eventDetailsMarketQueryService.getTagsFromQueryString();
        const newTags = [];

        for (const tag of tags.reverse()) {
            if (!this.tagExists(this.event.sitemapSets, tag) && this.isSitemapEnabled) {
                this.eventDetailsMarketQueryService.cleanQueryString(true);
            } else {
                newTags.push(tag);
            }
        }

        return newTags.reverse();
    }

    private tagExists(sitemapSets: EventSitemapSet[] = [], tag: string): boolean {
        if (
            Object.values(SetTab)
                .map((x) => x.toString())
                .includes(tag)
        ) {
            return true;
        }

        return !!sitemapSets.find((sitemapSet) => sitemapSet.id.toString() === tag || this.tagExists(sitemapSet.children, tag));
    }

    private setBetBuilderSubNavigation(tab: EventMarketsTabBarItem, payload: SubNavigationPayload): void {
        if (!payload.subTabs.length) {
            return;
        }

        const entryPoint = { ...tab, showSeparator: true, id: payload.subTabs[0].id, parentTabId: tab.id };
        const [, ...betBuilderTabs] = payload.subTabs.map(({ id, name: title }) => ({ id, title }));

        this.setTabs = [entryPoint, ...betBuilderTabs];
        this.setTabs.forEach((t) => (t.isBetBuilderSubItem = true));
        this.sitemapComponent?.update({ items: this.setTabs });
    }

    private getTagsForItem(active: EventMarketsTabBarItem, queryString?: string): string[] {
        const currentQueryString = queryString ?? active.queryString;
        const tags = this.eventDetailsMarketQueryService.getTagsFromQueryString(currentQueryString);
        tags.shift();

        if (this.isSgpTabSelected(active)) {
            this.trySetDefaultSgpContent(tags);
        }

        return tags;
    }

    private trySetDefaultSgpContent(tags: string[]): void {
        const defaultLandingPill = this.setTabs.find((t) => t.defaultPill);
        if (defaultLandingPill && defaultLandingPill.id.toString() === AngstromTagIdValues.Popular) {
            tags.push(defaultLandingPill.id.toString());
        }
    }

    private isSgpTabSelected(active: EventMarketsTabBarItem): boolean {
        return active.id.toString() === AngstromTagIdValues.Sgp;
    }
}
